import React from "react";
import { AppStatus } from "../../common/requests/platform/endpoints";
import { C } from "../../common/theme";
import { CHART_COLORS } from "../../ui/chart";
import type { Tone } from "../../ui/StatusPill";
import { RangeKey, formatCount, formatDate, parseDateInput, resolveRange } from "../Sales/salesData";

export { formatCount };

/** The single series is ink; today's unfinished bar is the warm grey step. */
export const SERIES_COLOR = CHART_COLORS[0];

/** Status tone and label; rank sorts problems first. Bar colours for the sparkline. */
export const STATUS_META: Record<AppStatus, { label: string; tone: Tone; color: string; rank: number }> = {
  down: { label: "Down", tone: "danger", color: C.danger, rank: 0 },
  degraded: { label: "Degraded", tone: "warning", color: C.coral, rank: 1 },
  healthy: { label: "Healthy", tone: "success", color: C.leaf, rank: 2 },
};

/** Mirrors SLOW_MS in admin-backend health/base.py */
export const SLOW_SECONDS = 3;

/** "All time" asks for this many days; the API caps a range at 731. */
export const ALL_TIME_DAYS = 730;

const DEFAULT_TZ = "Asia/Singapore";
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;
  } catch {
    return DEFAULT_TZ;
  }
};

/* ── ranges ──────────────────────────────────────────────────────── */

export interface TrafficWindow {
  start: Date;
  /** Never later than now, so the comparison period has the same elapsed length */
  end: Date;
  label: string;
  /** The range runs past now, so views keep arriving */
  live: boolean;
}

/** Sales' date presets, with an open start ("All time") capped to ALL_TIME_DAYS. */
export function trafficWindow(key: RangeKey, from: string | undefined, to: string | undefined, now = new Date()): TrafficWindow {
  const range = resolveRange(key, from, to, now);
  const end = new Date(Math.min(range.end.getTime(), now.getTime()));
  const live = range.end.getTime() > now.getTime();
  if (range.start) return { start: range.start, end, label: range.label, live };
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (ALL_TIME_DAYS - 1));
  return { start, end, label: `All time · last ${ALL_TIME_DAYS} days`, live };
}

/* ── formatting ──────────────────────────────────────────────────── */

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n: number) => String(n).padStart(2, "0");

export const formatClock = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "14:05" today, "3 Oct 14:05" otherwise (fixed month names, see formatDate). */
export function formatWhen(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  if (sameDay(d, now)) return formatClock(d);
  const date = formatDate(d);
  return `${d.getFullYear() === now.getFullYear() ? date.replace(/ \d{4}$/, "") : date} ${formatClock(d)}`;
}

/** "Sun 4 Oct 2026" for a YYYY-MM-DD day key. */
export function formatDayLong(key: string): string {
  const d = parseDateInput(key);
  return d ? `${WEEKDAYS[d.getDay()]} ${formatDate(d)}` : key;
}

/** "4 Oct" for axis ticks. */
export function formatDayShort(key: string): string {
  const d = parseDateInput(key);
  return d ? formatDate(d).replace(/ \d{4}$/, "") : key;
}

export const isToday = (key: string, now = new Date()) => {
  const d = parseDateInput(key);
  return d !== null && sameDay(d, now);
};

export const formatMs = (ms: number | null | undefined) =>
  ms === null || ms === undefined || !Number.isFinite(ms) ? "–" : `${formatCount(Math.round(ms))} ms`;

/** Floors to 0.1% so one failed check never displays as 100.0%. */
export const formatUptime = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined ? "–" : `${(Math.floor(fraction * 1000) / 10).toFixed(1)}%`;

export const formatCompact = (n: number | null) =>
  n === null ? "" : new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);

const DEVICE_LABELS: Record<string, string> = {
  mobile: "Mobile",
  desktop: "Desktop",
  tablet: "Tablet",
  smarttv: "Smart TV",
  console: "Console",
  wearable: "Wearable",
  embedded: "Embedded",
  xr: "XR headset",
};
export const deviceLabel = (device: string | null | undefined) =>
  !device ? "Unknown" : DEVICE_LABELS[device.toLowerCase()] ?? device.charAt(0).toUpperCase() + device.slice(1);

/** "Couldn't load X: <the API's detail, or the network error>". */
export function errorMessage(e: any, what: string): string {
  const detail = e?.response?.data?.detail;
  const reason =
    typeof detail === "string" && detail
      ? detail
      : Array.isArray(detail) && detail[0]?.msg
        ? String(detail[0].msg)
        : e?.code === "ECONNABORTED"
          ? "the request timed out"
          : e?.message;
  return reason ? `${what}: ${reason}` : `${what}.`;
}

/* ── hooks ───────────────────────────────────────────────────────── */

/** Width of an element, kept current as the layout changes. */
export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = React.useRef<T | null>(null);
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/**
 * Calls `poll` now and then every `intervalMs`, but only while the tab is
 * visible: hiding the tab stops the timer, showing it again polls at once if
 * the last poll is stale. The next poll is scheduled when the previous one
 * finishes, so calls never overlap. Returns `trigger` for a manual poll,
 * which is ignored while one is in flight.
 */
export function useVisiblePolling(
  enabled: boolean,
  intervalMs: number,
  poll: (signal: AbortSignal, manual: boolean) => Promise<void>
): { trigger: () => void; busy: boolean } {
  const pollRef = React.useRef(poll);
  pollRef.current = poll;
  const timer = React.useRef<number | null>(null);
  const inFlight = React.useRef(false);
  const lastRun = React.useRef(0);
  const controller = React.useRef<AbortController | null>(null);
  const [busy, setBusy] = React.useState(false);

  const clear = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const run = React.useCallback(
    async (manual: boolean) => {
      if (inFlight.current) return;
      inFlight.current = true;
      clear();
      setBusy(true);
      const ac = new AbortController();
      controller.current = ac;
      try {
        await pollRef.current(ac.signal, manual);
      } finally {
        // An aborted run (unmount, StrictMode's double effect) was already
        // reset by the cleanup and must not mark the data fresh.
        if (controller.current === ac && !ac.signal.aborted) {
          inFlight.current = false;
          lastRun.current = Date.now();
          setBusy(false);
          if (document.visibilityState === "visible") {
            clear();
            timer.current = window.setTimeout(() => run(false), intervalMs);
          }
        }
      }
    },
    [intervalMs]
  );

  React.useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") {
        clear();
        return;
      }
      const age = Date.now() - lastRun.current;
      if (age >= intervalMs) {
        run(false);
      } else if (!inFlight.current) {
        clear();
        timer.current = window.setTimeout(() => run(false), intervalMs - age);
      }
    };
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clear();
      controller.current?.abort();
      controller.current = null;
      inFlight.current = false;
      setBusy(false);
    };
  }, [enabled, intervalMs, run]);

  return { trigger: React.useCallback(() => run(true), [run]), busy };
}
